import { Injectable, OnInit, Injector } from "@angular/core";
import { EventCategoryDeadlineDto, CreateOrEditRegistrationByAdminDto, RegistrationEventDto, OrderUserEditDto, MemberAnnuityUserMemberEditDto, UserAddressDto, UserPhoneDto, AnnuityEditDto, AnnuityCategoryDeadlineDto, MemberAnnuityDto, MemberCategoryEditDto, ValidateCouponOutput, UserEmailDto } from "@shared/service-proxies/service-proxies";
import { AppComponentBase } from "@shared/common/app-component-base";

import * as moment from 'moment';

export interface  IRegistrationCart {
    registration: CreateOrEditRegistrationByAdminDto,
    categoryDeadline: EventCategoryDeadlineDto,
    event: RegistrationEventDto
}

export interface  IMemberAnnuityCart {
    memberAnnuity: MemberAnnuityDto,
    categoryDeadline: AnnuityCategoryDeadlineDto,
    annuity: AnnuityEditDto
}

export interface ICart {
    registrations: IRegistrationCart[] | undefined,
    memberAnnuities: IMemberAnnuityCart[] | undefined,
    user: OrderUserEditDto | undefined,
    total: number | undefined,
    date: string | undefined,
    onLocal: boolean
}

@Injectable()
export class CartHelperService extends AppComponentBase {

    userLogged = this.appSession.user;

    constructor(
        injector: Injector
    ) {
        super(injector);
        this.checkCart();
    }

    checkCart(): void {

        let cart: ICart = this.loadCart();

        if (cart.date != moment().format('L') || (this.userLogged && cart && !cart.onLocal && cart.user && cart.user.id != this.userLogged.id)) {
            localStorage.removeItem('cart');
        }
    }

    loadCart(onLocal?: boolean): ICart {

        let cart: ICart = JSON.parse(localStorage.getItem('cart'));

        if (!cart || (!cart.onLocal && !!onLocal)) {

            this.clearCart();

            let newCart: ICart = {
                registrations: [],
                memberAnnuities: [],
                user: undefined,
                total: 0,
                date: moment().format('L'),
                onLocal: !!onLocal
            }
            cart = newCart;
        }

        return cart;
    }

    // TODO: Refatorar
    getCart(onLocal?: boolean): ICart {
        let cart: ICart = this.loadCart(onLocal);

        if (cart) {

            if (cart.user) {

                if (cart.user.member) {
                    Object.setPrototypeOf(cart.user.member, MemberAnnuityUserMemberEditDto.prototype);
                    cart.user.member.categories.forEach(category => {
                        Object.setPrototypeOf(category, MemberCategoryEditDto.prototype);
                    });
                }

                Object.setPrototypeOf(cart.user, OrderUserEditDto.prototype);
                Object.setPrototypeOf(cart.user.address, UserAddressDto.prototype);

                cart.user.emails.forEach(email => {
                    Object.setPrototypeOf(email, UserEmailDto.prototype);
                });

                cart.user.phones.forEach(phone => {
                    Object.setPrototypeOf(phone, UserPhoneDto.prototype);
                });
            }

            if (cart.registrations) {
                cart.registrations.forEach(registration => {
                    Object.setPrototypeOf(registration.registration, CreateOrEditRegistrationByAdminDto.prototype);
                    Object.setPrototypeOf(registration.categoryDeadline, EventCategoryDeadlineDto.prototype);

                    if (registration.registration.coupon)
                        Object.setPrototypeOf(registration.registration.coupon, ValidateCouponOutput.prototype);
                });
            }

            if (cart.memberAnnuities) {
                cart.memberAnnuities.forEach(memberAnnuity => {
                    Object.setPrototypeOf(memberAnnuity.annuity, AnnuityEditDto.prototype);
                    Object.setPrototypeOf(memberAnnuity.categoryDeadline, AnnuityCategoryDeadlineDto.prototype);
                });
            }
        }

        return cart;
    }

    addRegistrationToCart(item: IRegistrationCart, onLocal: boolean): void {

        let cart: ICart = this.loadCart(onLocal);

        if (cart.registrations.length > 0) {
            for (let i = 0; i < cart.registrations.length; i++) {
                if (cart.registrations[i].registration.eventId == item.registration.eventId) {
                    cart.registrations.splice(i, 1);
                    break;
                }
            }
        }

        item.registration.user = undefined;
        cart.registrations.push(item);

        this.save(cart);
    }

    removeRegistration(i: number): void {

        let cart: ICart = this.loadCart();
        cart.registrations.splice(i, 1);

        if (cart.registrations.length == 0 && cart.memberAnnuities.length == 0)
            this.clearCart();
        else
            this.save(cart);
    }

    removeRegistrationByEventId(id: number): void {

        let cart: ICart = this.loadCart();
        cart.registrations = cart.registrations.filter(x => x.registration.eventId != id);

        if (cart.registrations.length == 0 && cart.memberAnnuities.length == 0)
            this.clearCart();
        else
            this.save(cart);
    }

    getRegistration(eventId: number): IRegistrationCart {

        let registration: IRegistrationCart = undefined;
        let cart: ICart = this.loadCart();

        if (cart.registrations.length > 0) {
            for (let i = 0; i < cart.registrations.length; i++) {
                if (cart.registrations[i].registration.eventId == eventId) {
                    registration = cart.registrations[i];
                    break;
                }
            }
        }

        if (registration) {
            Object.setPrototypeOf(registration.categoryDeadline, EventCategoryDeadlineDto.prototype);

            if (registration.registration.coupon)
                Object.setPrototypeOf(registration.registration.coupon, ValidateCouponOutput.prototype);
        }

        return registration;
    }

    updateUserCart(user, onLocal: boolean): void {

        let cart: ICart = this.loadCart(onLocal);

        let userCart  = new OrderUserEditDto();
        userCart.member = user.member;
        userCart.userName = user.userName;
        userCart.name = user.name;
        userCart.nameForCredential = user.nameForCredential;
        userCart.emailAddress = user.emailAddress;
        userCart.emails = user.emails;
        userCart.phones = user.phones;
        userCart.address = user.address;
        userCart.id = user.id;
        cart.user = userCart;

        this.save(cart);
    }

    clearCart(): void {
        localStorage.removeItem('cart');
    }

    addMemberAnnuityToCart(item: IMemberAnnuityCart): void {

        let cart: ICart = this.loadCart();

        if (cart.memberAnnuities.length > 0) {
            for (let i = 0; i < cart.memberAnnuities.length; i++) {
                if (cart.memberAnnuities[i].memberAnnuity.annuityId == item.memberAnnuity.annuityId) {
                    cart.memberAnnuities.splice(i, 1);
                    break;
                }
            }
        }

        item.memberAnnuity.user = undefined;
        cart.memberAnnuities.push(item);

        this.save(cart);
    }

    removeMemberAnnuity(i: number): void {

        let cart: ICart = this.loadCart();
        cart.memberAnnuities.splice(i, 1);

        if (cart.registrations.length == 0 && cart.memberAnnuities.length == 0)
            this.clearCart();
        else
            this.save(cart);
    }

    removeMemberAnnuityById(id: number): void {

        let cart: ICart = this.loadCart();
        cart.memberAnnuities = cart.memberAnnuities.filter(x => x.memberAnnuity.annuityId != id);

        if (cart.registrations.length == 0 && cart.memberAnnuities.length == 0)
            this.clearCart();
        else
            this.save(cart);
    }

    getMemberAnnuity(annuityId: number): IMemberAnnuityCart {

        let memberAnnuity: IMemberAnnuityCart = undefined;
        let cart: ICart = this.loadCart();

        if (cart.memberAnnuities.length > 0) {
            for (let i = 0; i < cart.memberAnnuities.length; i++) {
                if (cart.memberAnnuities[i].memberAnnuity.annuityId == annuityId) {
                    memberAnnuity = cart.memberAnnuities[i];
                    break;
                }
            }
        }

        if (memberAnnuity)
            Object.setPrototypeOf(memberAnnuity.categoryDeadline, AnnuityCategoryDeadlineDto.prototype);

        return memberAnnuity;
    }

    save(cart: ICart): void {
        cart.total = cart.total = this.sumTotal(cart);
        localStorage.setItem('cart', JSON.stringify(cart));
    }

    sumTotal(cart: ICart): number {

        let total = 0;

        cart.registrations.forEach(x => {
            total += x.registration.payments[0].totalPaid;
        });

        cart.memberAnnuities.forEach(x => {
            total += x.memberAnnuity.payments[0].totalPaid;
        });

        return total;
    }

    finalizeCart(): void {
        this.clearCart();
        abp.event.trigger('app.cart.refresh');
        this.notify.info(this.l('SavedSuccessfully'));
    }
}



// WEBPACK FOOTER //
// ./src/app/store/cart/cart-helper-service.ts